import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { teamMembers, honors } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('提交成功！我们将尽快与您联系。');
      return;
    }
    formData.delete('phone_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4cot3pcjqs2fcslsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('提交成功！我们将尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试。';
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          setFormMsg('提交成功！我们将尽快与您联系。');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMsg(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <SchemaOrg type="Organization" data={{
        name: '强奸罪法律解析与辩护指南',
        url: typeof window !== 'undefined' ? window.location.origin : '',
        description: '专业刑事辩护律师团队，专注强奸罪等性侵犯罪辩护',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          streetAddress: '建国路88号SOHO现代城A座12层',
        },
        contactPoint: { '@type': 'ContactPoint', telephone: '400-888-9999', contactType: 'customer service' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }} />

      <Navbar />
      <main className="pt-20">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">关于我们</h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                强奸罪法律解析与辩护指南由资深刑事辩护律师团队创立，致力于为当事人及家属提供专业、全面、及时的强奸罪法律信息与辩护策略指导。
              </p>
            </div>

            <div className="space-y-5 text-sm leading-relaxed text-foreground-700">
              <p>
                我们深知，涉及强奸罪的当事人在面对刑事指控时，往往处于极度焦虑和无助的状态。法律知识的匮乏、对司法程序的不了解、对社会舆论的恐惧，都可能让当事人及家属陷入困境。正因如此，我们创建了这个专业法律信息平台，希望通过系统、清晰的法律解析，帮助每一位需要帮助的人了解自身权利、理解法律程序、找到正确的应对之道。
              </p>
              <p>
                我们的律师团队均具有十年以上的刑事辩护经验，曾成功办理数百起涉及强奸罪的刑事案件，积累了丰富的实务经验和深厚的法学理论功底。我们坚持以专业为基石、以客户为中心，在法律框架内竭尽全力为当事人争取最有利的结果。
              </p>
              <p>
                无论您是受害者寻求法律救济，还是被指控者需要专业辩护，亦或是家属希望了解案件走向和法律权利，我们都愿意为您提供真诚、专业、可靠的法律支持。
              </p>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-100/70">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">律师团队</h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">资深刑事辩护律师，用专业与经验守护您的合法权益</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                  <div className="h-48 bg-background-200 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20lawyer%20in%20formal%20business%20attire%2C%20confident%20expression%2C%20law%20office%20background%20with%20bookshelves%2C%20warm%20ambient%20lighting%2C%20clean%20professional%20look%2C%20sharp%20dark%20suit&width=400&height=480&seq=about-lawyer-wang-2026&orientation=portrait"
                      alt="王明远"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">王明远</h3>
                    <p className="text-xs text-primary-600 font-medium mb-2">主任律师 / 高级合伙人</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{teamMembers[0].description}</p>
                  </div>
                </div>
                <div className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                  <div className="h-48 bg-background-200 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20lawyer%20portrait%20in%20modern%20office%2C%20serious%20expression%2C%20law%20library%20background%2C%20warm%20natural%20lighting%2C%20clean%20and%20sharp%20look%2C%20formal%20navy%20suit&width=400&height=480&seq=about-lawyer-chen-2026&orientation=portrait"
                      alt="陈思远"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">陈思远</h3>
                    <p className="text-xs text-primary-600 font-medium mb-2">合伙人律师</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{teamMembers[1].description}</p>
                  </div>
                </div>
                <div className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                  <div className="h-48 bg-background-200 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20attorney%20portrait%20in%20elegant%20law%20firm%20setting%2C%20confident%20and%20approachable%20expression%2C%20bookshelves%20and%20scales%20of%20justice%20in%20background%2C%20soft%20ambient%20light%2C%20charcoal%20suit&width=400&height=480&seq=about-lawyer-lin-2026&orientation=portrait"
                      alt="林雪峰"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">林雪峰</h3>
                    <p className="text-xs text-primary-600 font-medium mb-2">资深律师</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{teamMembers[2].description}</p>
                  </div>
                </div>
                <div className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                  <div className="h-48 bg-background-200 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20lawyer%20portrait%20in%20modern%20office%2C%20warm%20confident%20smile%2C%20law%20books%20and%20documents%20on%20desk%20in%20background%2C%20soft%20natural%20lighting%2C%20elegant%20professional%20attire&width=400&height=480&seq=about-lawyer-zhao-2026&orientation=portrait"
                      alt="赵雅文"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">赵雅文</h3>
                    <p className="text-xs text-primary-600 font-medium mb-2">律师 / 心理咨询师</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{teamMembers[3].description}</p>
                  </div>
                </div>
              </div>
          </div>
        </section>

        {/* Honors */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">资质荣誉</h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">多年专业积累，获得业界广泛认可</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {honors.map((honor, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 rounded-lg bg-background-100/70 border border-background-200/70">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-award-line"></i>
                  </div>
                  <span className="text-sm text-foreground-800 font-medium">{honor}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-100/70">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">如需法律咨询，请通过以下方式与我们取得联系</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1">电话咨询</h4>
                    <p className="text-sm text-foreground-600">400-888-9999</p>
                    <p className="text-xs text-foreground-400">工作日 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1">电子邮件</h4>
                    <p className="text-sm text-foreground-600">contact@criminaldefense.cn</p>
                    <p className="text-xs text-foreground-400">我们会在24小时内回复</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1">办公地址</h4>
                    <p className="text-sm text-foreground-600">北京市朝阳区建国路88号</p>
                    <p className="text-sm text-foreground-600">SOHO现代城A座12层</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名 <span className="text-red-500">*</span></label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">电话 <span className="text-red-500">*</span></label>
                      <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电话"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱（选填）"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">咨询内容 <span className="text-red-500">*</span></label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请简要描述您的情况和法律需求，我们会尽快回复（限500字）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    提交咨询
                  </button>
                  {formStatus !== 'idle' && (
                    <p className={`text-xs text-center ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                      {formMsg}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}